// src/utils/visibilityFilter.js

/**
 * Filters an array of diff objects based on the displayColumns specified in the visibility configuration.
 * Each diff object is expected to have a 'field' property representing the field name.
 * @param {Array<Object>} diffArray - Array of diff objects (e.g., from compareObjectsWithDiffs).
 * @param {string} sObjectName - The Salesforce API name of the object these diffs belong to (e.g., "Product2").
 * @param {Object} visibilityConfig - The global visibility configuration { cpq: [...], rc: [...] }.
 * @param {('cpq'|'rc')} configType - Specifies which part of the config to use.
 * @returns {Array<Object>} A new array containing only diffs for allowed fields.
 */
export const filterDiffsByDisplayColumns = (diffArray, sObjectName, visibilityConfig, configType) => {
  if (!Array.isArray(diffArray)) {
    return diffArray;
  }

  if (!visibilityConfig || typeof visibilityConfig !== 'object') {
    return diffArray;
  }

  const configSection = visibilityConfig[configType];
  if (!configSection || !Array.isArray(configSection)) {
    return diffArray;
  }

  const objectConfig = configSection.find((cfg) => cfg.objectName === sObjectName);
  if (!objectConfig || typeof objectConfig.displayColumns !== 'string') {
    // If no specific config is found for this object, return the original array.
    return diffArray;
  }

  const displayColumnsString = objectConfig.displayColumns.trim();

  if (displayColumnsString === '') {
    // If displayColumns is explicitly empty, show no fields.
    return [];
  }

  const displayColumnsArray = displayColumnsString
    .split(',')
    .map((col) => col.trim().toLowerCase())
    .filter((col) => col);

  if (displayColumnsArray.length === 0) {
    // If displayColumns contains only whitespace or commas, show no fields.
    return [];
  }

  const filteredResult = diffArray.filter((diffItem) => {
    if (diffItem && typeof diffItem.field === 'string') {
      const fieldToMatch = diffItem.field.toLowerCase();
      const isIncluded = displayColumnsArray.includes(fieldToMatch);
      return isIncluded;
    }
    // Keep malformed items by default, or change to false to discard them.
    return true;
  });

  return filteredResult;
};
